import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, PhoneCall, TrendingUp, Coins, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { inr, numIN } from "@/lib/format";

function TargetCard({ icon: Icon, label, value, target, isMoney, testid }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const hit = target && value >= target;
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={testid}>
      <Icon className={`h-4 w-4 mb-2 ${hit ? "text-emerald-600" : "text-amber-600"}`} />
      <div className="font-mono-num text-2xl font-bold">{isMoney ? inr(value) : numIN(value)}</div>
      <div className="text-xs font-medium">{label}</div>
      {target != null && (
        <>
          <div className="text-xs text-muted-foreground mt-0.5">of {isMoney ? inr(target) : numIN(target)} target</div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className={`h-full rounded-full ${hit ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </>
      )}
    </div>
  );
}

export default function WeeklyTracker() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/weekly").then(({ data }) => {
      setData(data);
      if (data.weeks?.length) setSelected(data.weeks[data.weeks.length - 1].week);
    });
  }, []);

  if (!data) {
    return <div className="h-40 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin" /></div>;
  }

  const weeks = data.weeks || [];
  const targets = data.targets || {};
  const cur = weeks.find((w) => w.week === selected);

  if (weeks.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="weekly-empty">
        No weekly data yet. Log your daily activity to see week-by-week performance.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Week selector */}
      <div className="flex flex-wrap gap-2" data-testid="week-selector">
        {weeks.map((w) => (
          <Button key={w.week} size="sm" variant={w.week === selected ? "default" : "outline"}
                  className={w.week === selected ? "bg-emerald-600 hover:bg-emerald-700 text-white font-mono-num" : "font-mono-num"}
                  onClick={() => setSelected(w.week)} data-testid={`week-btn-${w.week}`}>
            W{w.week}
          </Button>
        ))}
      </div>

      {/* Selected week comparison vs target */}
      {cur && (
        <motion.div key={selected} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    className="space-y-4" data-testid="weekly-selected">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Week {cur.week} · {cur.days} {cur.days === 1 ? "day" : "days"} logged</div>
            <div className="flex items-center gap-1 text-sm font-mono-num font-bold text-emerald-700" data-testid="weekly-points">
              <Star className="h-4 w-4" /> {numIN(cur.points)} pts
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <TargetCard icon={Users} label="Reached" value={cur.people} target={targets.weekly_people} testid="weekly-card-people" />
            <TargetCard icon={PhoneCall} label="Session 0s" value={cur.session0} target={targets.weekly_session0} testid="weekly-card-session0" />
            <TargetCard icon={TrendingUp} label="Clients joined" value={cur.clients} target={null} testid="weekly-card-clients" />
            <TargetCard icon={Coins} label="Money received" value={cur.money} target={targets.weekly_money} isMoney testid="weekly-card-money" />
          </div>
        </motion.div>
      )}

      {/* Full weekly review table */}
      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-x-auto">
        <div className="px-5 pt-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Weekly review</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Week</TableHead>
              <TableHead className="text-right">Reached</TableHead>
              <TableHead className="text-right">Session 0s</TableHead>
              <TableHead className="text-right">Clients</TableHead>
              <TableHead className="text-right">Money</TableHead>
              <TableHead className="text-right">Points</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {weeks.map((w) => (
              <TableRow key={w.week} data-testid={`weekly-row-${w.week}`}
                        className={`cursor-pointer ${w.week === selected ? "bg-emerald-50" : ""}`}
                        onClick={() => setSelected(w.week)}>
                <TableCell className="font-mono-num font-semibold">W{w.week}</TableCell>
                <TableCell className="text-right font-mono-num">{numIN(w.people)}</TableCell>
                <TableCell className="text-right font-mono-num">{numIN(w.session0)}</TableCell>
                <TableCell className="text-right font-mono-num text-emerald-700 font-semibold">{numIN(w.clients)}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(w.money)}</TableCell>
                <TableCell className="text-right font-mono-num font-bold text-emerald-700">{numIN(w.points)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
